import { TFPageContent, TFPageHeader } from '@tf/shared';
import { Box, createStyles, Tabs } from '@tf/ui';
import { T } from '@tf/utils';
import type React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { appStore } from '@/core/stores';
import { UserCapabilities } from '@/core/types';
import { useCurrentUserCapabilityCheck } from '@/core/utils';

const TABS = [
	{ href: '/settings/cra', title: 'CRA settings' },
	{ href: '/settings/cra/auto-assigned', title: 'Auto-assigned settings' },
] as const;

const useStyles = createStyles(({ colors }) => ({
	tabs: {
		marginTop: '-.75rem',
	},
	tabsList: {
		borderBottomWidth: 1,
		borderColor: colors.gray[2],
		borderBottomStyle: 'solid',
	},
	tab: {
		display: 'flex',
		gap: '.35rem',
	},
}));

export const CRASettingsLayout: React.FC = () => {
	const { classes } = useStyles();

	const navigate = useNavigate();
	const { pathname } = useLocation();

	const tenant = appStore.tenant?.get();
	T.assertNotNullish(tenant, 'no tenant in appStore');
	const canManageUsers = useCurrentUserCapabilityCheck(UserCapabilities.CAN_MANAGE_USERS);

	return (
		<>
			<TFPageHeader
				title="CRA settings"
				tenant={tenant}
				breadcrumbs={[
					...(canManageUsers ? [{ href: '/settings/users', title: 'Workspace settings' }] : []),
					TABS.find((t) => t.href === pathname) ?? TABS[0],
				]}
			/>
			<TFPageContent>
				<Tabs
					variant="normal"
					value={pathname}
					onChange={(value) => {
						if (value) {
							navigate(value);
						}
					}}
					styles={({ colors }) => ({
						root: { flexGrow: 1, borderBottomWidth: 2 },
						tab: {
							padding: '.65rem 1rem',
							borderBottomWidth: 1.5,
							borderRadius: 0,
							"&[data-active='true']": {
								fontWeight: 500,
								color: colors.brand[6],
								backgroundColor: colors.brand[0],
								borderBottom: `1.5px solid ${colors.brand[6]}`,
							},
						},
						list: { borderBottom: `1px solid ${colors.gray[2]}` },
					})}
					className={classes.tabs}
				>
					<Tabs.List className={classes.tabsList}>
						{TABS.map(({ href, title }) => (
							<Tabs.Tab key={href} value={href}>
								<Box className={classes.tab}>{title}</Box>
							</Tabs.Tab>
						))}
					</Tabs.List>
					<Box pt="0.75rem">
						<Outlet />
					</Box>
				</Tabs>
			</TFPageContent>
		</>
	);
};
